import {
  type DateRange,
  isSameDateRangeByDay,
} from "@prismicio/editor-support/Date"
import { keys } from "@prismicio/editor-support/Object"
import { Box, Separator } from "@prismicio/editor-ui"
import { Fragment, type ReactNode, Suspense, useState } from "react"

import { getCurrentBillingPeriod } from "../billing/billingUtils"
import { useBillingPeriodsQuery } from "../billing/useBillingPeriods"
import {
  BillingPeriodPicker,
  BillingPeriodPickerSkeleton,
} from "./BillingPeriodPicker"
import {
  UsageSummaryItem,
  UsageSummaryItemSkeleton,
  type UsageSummaryItemSkeletonProps,
} from "./UsageSummaryItem"
import { usageSummaryItemTypes } from "./usageSummaryItemTypes"

export function UsageSummary() {
  const { data: billingPeriods } = useBillingPeriodsQuery()

  const currentBillingPeriod = getCurrentBillingPeriod(billingPeriods)
  const [selectedDateRange, setSelectedDateRange] =
    useState<DateRange>(currentBillingPeriod)

  const planLimitsKnown = isSameDateRangeByDay(
    selectedDateRange,
    currentBillingPeriod,
  )

  return (
    <UsageSummaryTemplate
      renderUsageSummaryItem={(props) => (
        <UsageSummaryItem
          {...props}
          selectedDateRange={selectedDateRange}
          planLimitsKnown={planLimitsKnown}
        />
      )}
      billingPeriodPicker={
        <Suspense fallback={<BillingPeriodPickerSkeleton />}>
          <BillingPeriodPicker
            dateRange={selectedDateRange}
            onDateRangeChange={setSelectedDateRange}
          />
        </Suspense>
      }
    />
  )
}

interface UsageSummaryTemplateProps {
  renderUsageSummaryItem: (props: UsageSummaryItemSkeletonProps) => ReactNode
  billingPeriodPicker: ReactNode
}

function UsageSummaryTemplate(props: UsageSummaryTemplateProps) {
  const { renderUsageSummaryItem, billingPeriodPicker } = props
  const itemTypeKeys = keys(usageSummaryItemTypes)
  return (
    <Box flexDirection="column" gap={24}>
      {itemTypeKeys.map((itemTypeKey, index) => {
        const item = renderUsageSummaryItem({
          itemTypeKey,
          billingPeriodPicker: index === 0 ? billingPeriodPicker : undefined,
        })
        const isLastItem = index === itemTypeKeys.length - 1
        return (
          <Fragment key={itemTypeKey}>
            {item}
            {!isLastItem ? <Separator style="dashed" /> : undefined}
          </Fragment>
        )
      })}
    </Box>
  )
}

export function UsageSummarySkeleton() {
  return (
    <UsageSummaryTemplate
      renderUsageSummaryItem={UsageSummaryItemSkeleton}
      billingPeriodPicker={<BillingPeriodPickerSkeleton />}
    />
  )
}
